import { Box, debounce, Stack, Typography, Theme } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import ChargingBatteryIcon from '../../../../assets/icons/chargingBatteryIcon.svg?react';
import { CustomInput } from '../../../../components/common/CustomInput/CustomInput';
import { OcppService } from '../../../../services/ocpp/OcppService';
import { InputValueBlockProps } from './types';
import { Pause } from '../../../../components/common/Pause/Pause';
import { ConnectorStatus } from '../../../../common/enums/connectorStatus';

const presetValues = ['5', '15', '30'];

const ocppService = new OcppService();

export const InputValueBlock: React.FC<InputValueBlockProps> = ({
  id,
  inputValue,
  setInputValue,
  setToPay,
  connectorStatus,
  connectorId,
}) => {
  const { t } = useTranslation();

  const calculate = async (kwt: number) => {
    if (kwt > 0) {
      const response = await ocppService.calculate(id, kwt);

      setToPay({
        kwt: response.kwt,
        amount: +response.amount,
        serviceFee: +response.serviceFee || 0,
        time: +response.time,
        balanceDeduction: +response?.balanceDeduction || 0,
      });
    }
  };

  const debouncedCalculate = useCallback(
    debounce((value: string) => {
      if (value) {
        calculate(parseInt(value));
      }
    }, 500),
    [],
  );

  const handleChange = (raw: string) => {
    // kWh is a whole number of units. Anything else that reaches the field —
    // a pasted string, a desktop keyboard, iOS dictation — used to be stored
    // and then handed to `parseInt`, which quietly produced NaN and left the
    // driver looking at text the page would not price.
    const value = raw.replace(/\D/g, '');
    setInputValue(value);

    if (!value || value === '0') {
      setToPay(null);
    } else {
      debouncedCalculate(value);
    }
  };

  // Show Pause component if connector is suspended
  if (connectorStatus === ConnectorStatus.Suspended_EV || connectorStatus === ConnectorStatus.Suspended_EVSE) {
    return <Pause status={connectorStatus} connectorId={connectorId} />;
  }

  return (
    <Stack
      sx={(theme: Theme) => ({
        padding: { xs: '10px 30px' },
        backgroundColor: theme.palette.gray[200],
        borderRadius: '10px',
        gap: { xs: '8px' },
        alignItems: 'center',
      })}
    >
      <Typography variant="body1" color="common.white">
        {t('orderPage.selectTheNumberOfkwt')}
      </Typography>
      <Stack direction="row" sx={{ gap: { xs: '7px' } }} alignItems="center">
        <ChargingBatteryIcon />
        <Box sx={{ width: '40px' }}>
          <CustomInput
            fieldName=""
            label=""
            // A number of kWh, so the digits keypad — this field was opening
            // the full qwerty keyboard, and with nothing declared iOS also
            // offered to autofill a password or a card into it.
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            enterKeyHint="done"
            onChange={(e) => handleChange(e.target.value)}
            value={inputValue}
            shortInput
          />
        </Box>
        <Typography variant="h1" color="common.white">
          {t('orderPage.energyUnit')}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ marginTop: { xs: '20px' }, gap: { xs: '26px' } }}
      >
        {presetValues.map((item, index) => (
          <Box
            key={index}
            sx={(theme: Theme) => ({
              backgroundColor: theme.palette.gray[50],
              padding: { xs: '0 12px' },
              borderRadius: '6px',
            })}
            onClick={() => handleChange(item)}
          >
            {item}
          </Box>
        ))}
      </Stack>
      {/*
        The number chosen here is pre-paid in full at StartTransaction, which
        makes asking for more than you need look like a loss. It isn't: the
        unconsumed part is refunded to the balance when the session settles
        (see the deductedAmount reconciliation in ocpp.gateway). Say so here,
        where the driver is deciding the number, rather than leaving them to
        find out afterwards.
      */}
      <Typography
        variant="body2"
        // gray.100, not the gray.50 used for muted text elsewhere: this block
        // sits on gray[200], where gray.50 lands around 3.2:1 — under AA for
        // 12px type, and this is the line that tells the driver their money
        // is not at risk.
        color="gray.100"
        textAlign="center"
        sx={{ marginTop: '12px', lineHeight: 1.45 }}
      >
        {t('orderPage.unusedKwtReturned')}
      </Typography>
    </Stack>
  );
};
